import {
  displayCentsInDollars,
  lightTheme,
  LocationServiceValue,
  LocationServiceValueBrand,
  LocationServiceValueClient,
} from '@vgrubs/components';
import { countOf } from './serviceValueLines';

export type ValuePartKey = 'payout' | 'vDriveSavings' | 'recoveries';

/** One of the three things a client makes or saves with us. */
export type ValuePart = {
  key: ValuePartKey;
  label: string;
  /** Cents. */
  amount: number;
  /** What the amount stands on: "9 orders", "46 deliveries". */
  count: number;
  countLabel: string;
};

/**
 * Each part keeps its colour everywhere, in the bar and beside its line:
 * green like Total Sales, gold like Total Recovered in the Overview under
 * the cards, and the theme blue for vDrive. Checked as a set for colour
 * blindness; green is under 3:1 on white, so every segment also has its
 * amount written out next to it.
 *
 * A function, not a constant: reading lightTheme from the barrel at module
 * top level breaks files the app loads early.
 */
export const partColor = (key: ValuePartKey) =>
  ({
    payout: lightTheme.green,
    vDriveSavings: lightTheme.blue,
    recoveries: lightTheme.darkYellow,
  }[key]);

type Source = Pick<
  LocationServiceValueBrand,
  | 'orders'
  | 'payout'
  | 'vDriveSavings'
  | 'vDriveDeliveries'
  | 'recoveries'
  | 'recoveredOrders'
>;

/**
 * The parts of a total, always in the same order. `withPayout` is false for
 * a brand that is not one of our virtual brands: it has no payout at all,
 * rather than a payout of $0.
 */
export const valueParts = (
  source: Source,
  { withPayout = true }: { withPayout?: boolean } = {}
): ValuePart[] => [
  ...(withPayout
    ? [
        {
          key: 'payout' as const,
          label: 'Payout',
          amount: source.payout,
          count: source.orders,
          countLabel: countOf(source.orders, 'order'),
        },
      ]
    : []),
  {
    key: 'vDriveSavings',
    label: 'vDrive savings',
    amount: source.vDriveSavings,
    count: source.vDriveDeliveries,
    countLabel: countOf(source.vDriveDeliveries, 'delivery', 'deliveries'),
  },
  {
    key: 'recoveries',
    label: 'Recovered',
    amount: source.recoveries,
    count: source.recoveredOrders,
    countLabel: countOf(source.recoveredOrders, 'order'),
  },
];

/** What the client's three lines say behind their info icons. */
export const clientPartInfo = (client: LocationServiceValueClient) => ({
  payout: `What the location was paid for its virtual brands' orders: ${countOf(
    client.orders,
    'order'
  )}, ${displayCentsInDollars(client.sales)} in sales.`,
  vDriveSavings: `Estimated vDrive Savings of ${countOf(
    client.vDriveDeliveries,
    'delivery',
    'deliveries'
  )}, the same figure each order shows.`,
  recoveries: `Revenue Recovery on ${countOf(
    client.recoveredOrders,
    'order'
  )}.`,
});

/** The parts as ValueBar segments, each in its own colour. */
export const partSegments = (parts: ValuePart[]) =>
  parts.map((part) => ({
    key: part.key,
    label: part.label,
    amount: part.amount,
    color: partColor(part.key),
  }));

/**
 * A brand's share of the client's total: payout, savings and recoveries.
 * A negative part (a refund) counts as nothing, so the bar never runs
 * backwards.
 */
export const partsTotal = (parts: ValuePart[]) =>
  parts.reduce((sum, part) => sum + Math.max(0, part.amount), 0);

type Company = LocationServiceValue['company'];

/**
 * vGrubs Profit's bar: what is left of the income once the location's costs
 * are paid (green), and those costs (grey). A loss turns it round: how much
 * of the costs the income covered (grey) and the rest in red.
 */
export const profitBar = ({ income, expenses, profit }: Company) =>
  profit >= 0
    ? {
        segments: [
          {
            key: 'profit',
            label: 'Profit',
            amount: profit,
            color: lightTheme.green,
          },
          {
            key: 'expenses',
            label: 'Expenses',
            amount: expenses.total,
            color: lightTheme.lightGray,
          },
        ],
        scale: income.total,
      }
    : {
        segments: [
          {
            key: 'covered',
            label: 'Income',
            amount: income.total,
            color: lightTheme.lightGray,
          },
          {
            key: 'loss',
            label: 'Loss',
            amount: -profit,
            color: lightTheme.primary,
          },
        ],
        scale: expenses.total,
      };

/** "54% margin" beside the title; none without income to take it from. */
export const marginTag = ({ income, profit }: Company) => {
  if (income.total <= 0) return null;
  const margin = Math.round((profit / income.total) * 100);
  return margin < 0
    ? {
        label: `${margin}% margin`,
        color: lightTheme.primary,
        background: lightTheme.transparentRed,
      }
    : {
        label: `${margin}% margin`,
        color: lightTheme.green,
        background: lightTheme.transparentGreen,
      };
};
